import React, { forwardRef, useRef } from 'react';
import classnames from 'classnames';
import { getSysFieldid } from '@platform/api';
import ColGroup from './ColGroup';
import Blank from './Blank';
import { getGlobalStorage } from '@platform/api';

const windowForSonar = window;

export default forwardRef(function TableHeader(props, ref) {
    /**
     * —————————————————————————————————————— hooks ——————————————————————————————————————
     */
    let startX = useRef(0),
        startWidth = useRef(0);
    /**
     * —————————————————————————————————————— render ——————————————————————————————————————
     */
    //自动化标志位 0关 1开
    let ncc_automated_open = getGlobalStorage('localStorage', '_ncc_automated_') === '1';
    let {
        rowHeight,
        columnInfo,
        isFixLeft,
        isFixRight,
        onColumnResize,
        hideHeader,
        scrollBarSize,
        hasVerticalScrollBar,
        bodyWidth = ncc_automated_open ? '100%' : undefined,
        fixedTop,
        cellProps: getCellProps,
        rows: tableRows,
        horizontalVirtualInfo,
        virtualConfig,
        setHeadClass,
        cellSelectConfig = {},
    } = props;

    !hasVerticalScrollBar && (scrollBarSize = 0);

    if (hideHeader) {
        return <div
            ref={ref}
            className="table-wrapper table-next-header-wrapper"
        />;
    }

    let {
            flatColumns,
            left: { columns: leftColumns, offsets: leftOffsets, leftColumnsMap },
            right: { columns: rightColumns, offsets: rightOffsets, rightColumnsMap },
            middle: { columns: middleColumns },
            leaf: { columnsWidth, columnsWidthMap, columns: leafColumns },
            headerRowsCount,
            formatColumnMap,
            rawColumns,
        } = columnInfo,
        { copiedCell = new Map() } = cellSelectConfig;

    if (!rawColumns?.length) {
        return <div
            ref={ref}
            className="table-wrapper table-next-header-wrapper"
        />;
    }

    let rows = [...new Array(headerRowsCount)].map(() => []),
        leftSpan = [...new Array(headerRowsCount)].map(() => ({})),
        rightSpan = [...new Array(headerRowsCount)].map(() => ({}));

    let {
            startRenderIndex: leftViewIndex,
            endRenderIndex: rightViewIndex,
        } = horizontalVirtualInfo,
        leftCount = leftColumns.length,
        middleCount = middleColumns.length,
        rightCount = rightColumns.length,
        firstShowColumn, lastShowColumn, realFirstShowColumn, realLastShowColumn;

    leftViewIndex += leftCount;
    rightViewIndex += leftCount;

    if (!virtualConfig?.header) {
        leftViewIndex = 0;
        rightViewIndex = rawColumns?.[rawColumns.length - 1].viewIndex + leftCount;
    }

    if (virtualConfig?.header && leftCount < rawColumns?.length && rightCount < rawColumns?.length) {
        // 最左显示列
        for (let i = leftCount; i < leafColumns.length; i++) {
            if (leafColumns[i]?.range?.[1] > leftViewIndex && leafColumns[i]?.range?.[0] >= leftViewIndex) {
                let { dataIndex, colSpanDataIndex } = leafColumns[i];
                if (dataIndex === colSpanDataIndex) {
                    firstShowColumn = leafColumns[i];
                } else {
                    firstShowColumn = leafColumns[Math.max(leftCount, i - dataIndex + colSpanDataIndex)];
                }
                realFirstShowColumn = leafColumns[i];
                break;
            }
        }

        // 最右显示列
        for (let i = leafColumns.length - 1 - rightCount; i >= 0; i--) {
            if (leafColumns[i]?.range?.[0] <= rightViewIndex && leafColumns[i]?.range?.[1] >= rightViewIndex) {
                let { dataIndex, colSpanDataIndex } = leafColumns[i];
                if (dataIndex === colSpanDataIndex) {
                    lastShowColumn = leafColumns[i];
                } else {
                    lastShowColumn = leafColumns[Math.min(leafColumns.length - 1 - rightCount, i - dataIndex + colSpanDataIndex)];
                }
                realLastShowColumn = leafColumns[i];
                break;
            }
        }

        let node = firstShowColumn;
        // 不是父列的第一个子节点时，计算本行需要合并的单元格占位
        while (node?.path.length > 1) {
            let { path, depth, height } = node,
                parentKey = path[path.length - 2],
                parentColumn = formatColumnMap[parentKey];

            if (node?.range?.[0] !== parentColumn?.range?.[0]) {
                let colSpan = Math.max(realFirstShowColumn?.range?.[0], node?.range?.[0], 0) - parentColumn?.range?.[0];
                leftSpan[depth] = { colSpan: colSpan, rowSpan: headerRowsCount - depth - height };
            }
            node = parentColumn;
        }

        // 一级列的左边不显示列合并成一个大单元格
        if (node?.range?.[0] !== formatColumnMap[rawColumns[leftCount]?.key]?.range?.[0]) { // formatColumnMap 统一数据源 leafColumns存在固定列时range会变
            leftSpan[0] = { colSpan: node?.range?.[0] - formatColumnMap[rawColumns[leftCount]?.key]?.range?.[0], rowSpan: headerRowsCount };
        }

        node = lastShowColumn;
        // 不是父列的最后一个子节点时，计算本行需要合并的单元格占位
        while (node?.path.length > 1) {
            let { path, depth, height } = node,
                parentKey = path[path.length - 2],
                parentColumn = formatColumnMap[parentKey];

            if (node?.range?.[1] !== parentColumn?.range?.[1]) {
                rightSpan[depth] = { colSpan: parentColumn?.range?.[1] - Math.min(realLastShowColumn?.range?.[1], node?.range?.[1], flatColumns.length), rowSpan: headerRowsCount - depth - height };
            }
            node = parentColumn;
        }
        let renderLastColumn = formatColumnMap[rawColumns[rawColumns.length - 1 - rightCount]?.key],
            { colSpan = 1 } = node;
        // 一级列的右边不显示列合并成一个大单元格 空白列代表全部都显示
        if (node?.key !== 'BLANK_COL' && renderLastColumn?.range?.[1] !== (node?.range?.[1] + colSpan - 1)) { // formatColumnMap 统一数据源 leafColumns存在固定列时range会变
            rightSpan[0] = { colSpan: renderLastColumn?.range?.[1] - node?.range?.[1] - colSpan + 1, rowSpan: headerRowsCount };
        }
    }

    let renderColumnProps = {}, currentMergeIndex = -1;

    flatColumns.forEach((column, index) => {
        let { height = 0, depth = 0, key, title, childLeafNodes, range, visible = true, itemtype, isleaf, resizable, colSpan, colSpanDataIndex, dataIndex } = column;

        let style = { height: rowHeight }, className = setHeadClass?.(column);

        let textClassName = "text-left";
        if (column.alignment == '1') {
            textClassName = "text-left";
        } else if (column.alignment == '2') {
            textClassName = "text-right";
        } else if (column.alignment == '3') {
            textClassName = "text-center";
        }

        if (index < leftCount) {
            // 左侧固定列
            style = {
                ...style,
                left: leftOffsets[index],
                // position: sticky时，右列会覆盖左列的拖拽条，故让左列index大右列1
            };
            className = classnames(
                className,
                'fixed-left',
                {
                    'is-fixing': isFixLeft,
                    'last-left-column': index === leftColumns.length - 1,
                },
                'sticky',
            );
        } else if (index >= leftCount + middleCount) {
            // 右侧固定列
            style = {
                ...style,
                right: rightOffsets[index - leftCount - middleCount] + scrollBarSize,
            };
            className = classnames(
                className,
                'fixed-right',
                {
                    'is-fixing': isFixRight,
                    'first-right-column': index === leftCount + middleCount,
                    'last-right-column': index === flatColumns.length - 1,
                },
                'sticky',
            );
        }

        let rowIndex = 0 - headerRowsCount + depth;
        let cellProps = getCellProps?.({ row: { key: rowIndex }, rowIndex, rows: tableRows }, { col: column, columns: leafColumns, colIndex: column.leafIndex }) ?? {};

        renderColumnProps[dataIndex] = {
            style,
            key,
            colKey: key,
            rowSpan: isleaf ? headerRowsCount - depth - height : 1,
            fieldid: getSysFieldid(key),
            ...cellProps,
            className: classnames(
                className,
                cellProps.className,
            ),
            itemtype,
            height: rowHeight,
            title,
            range,
            colSpan,
        };

        if (
            visible && /* 可见列 */
            (!virtualConfig?.header || (
                (leftColumnsMap[column.key] || rightColumnsMap[column.key]) || /* 固定列 */
                (range?.[1] > leftViewIndex && range?.[0] < rightViewIndex) /* 在视窗内 */
            ))
        ) {
            if (colSpanDataIndex === dataIndex) {
                let { itemtype, height, title, range, colSpan = 1, ...others } = renderColumnProps[dataIndex];
                currentMergeIndex = dataIndex;

                rows[depth].push(
                    <th {...others} colSpan={childLeafNodes?.length || colSpan || 1}>
                        <div className={`table-next-header-cell header-type-${itemtype} ${textClassName}`} style={{ height }}>{title}</div>
                        {resizable && renderDragHandler([range[0], range[1] + colSpan - 1])}
                        {/* {copiedCell.get(-1)?.has(column.key) && <div className="copy-border-flow"></div>} */}
                    </th>,
                );
            } else if (colSpanDataIndex !== currentMergeIndex) {
                currentMergeIndex = colSpanDataIndex;
                let { itemtype, height, title, range, colSpan, ...others } = renderColumnProps[colSpanDataIndex];

                rows[depth].push(
                    <th {...others} colSpan={childLeafNodes?.length || colSpan || 1}>
                        <div className={`table-next-header-cell header-type-${itemtype} ${textClassName}`} style={{ height }}>{title}</div>
                        {resizable && renderDragHandler([range[0], range[1] + colSpan - 1])}
                        {/* {copiedCell.get(-1)?.has(column.key) && <div className="copy-border-flow"></div>} */}
                    </th>,
                );
            }

        }
    });

    // 解决视窗内只有一行有数据，高度会变的问题
    let hasEmptyRows = rows.some(ths => !ths.length);

    //固定列需要fixed，只会出现在rows[0]
    leftSpan[0].colSpan && rows[0].splice(
        leftCount,
        0,
        <Blank type='th' key="_left_blank_0" className='_left_blank_' colSpan={leftSpan[0].colSpan} rowSpan={leftSpan[0].rowSpan}></Blank>,
    );
    rightSpan[0].colSpan && rows[0].splice(
        rows[0].length - rightCount,
        0,
        <Blank type='th' key="_right_blank_0" className='_right_blank_' colSpan={rightSpan[0].colSpan} rowSpan={rightSpan[0].rowSpan}></Blank>,
    );

    // 除首行外，合并单元格添加到首尾即可
    for (let i = 1; i < headerRowsCount; i++) {
        leftSpan[i].colSpan && rows[i].unshift(<Blank type='th' key={`_left_blank_-${i}`} className='_left_blank_' colSpan={leftSpan[i].colSpan} rowSpan={leftSpan[i].rowSpan}></Blank>);
        rightSpan[i].colSpan && rows[i].push(<Blank type='th' key={`_right_blank_-${i}`} className='_right_blank_' colSpan={rightSpan[i].colSpan} rowSpan={rightSpan[i].rowSpan}></Blank>);
    }

    return <>
        <div
            ref={ref}
            className={classnames(
                "table-wrapper",
                "table-next-header-wrapper",
                "u-table-header", /* 最后一个是自动化标记 */
                { 'fix-top': fixedTop },
            )}
            style={{ width: fixedTop ? bodyWidth : 'auto' }}
        >
            {/* <div
                ref={ref}
                className={classnames(
                    "table-wrapper",
                )}
            > */}
            <table className="table-next-header">
                {bodyWidth ? <ColGroup {...props}></ColGroup> : null}
                {/* eslint-disable-next-line react/jsx-key */}
                <thead>{rows.map(ths => <tr style={hasEmptyRows ? { height: rowHeight } : {}}>{bodyWidth ? ths : <th style={{ height: rowHeight }}></th>}</tr>)}</thead>
                {/* 有bodyWidth渲染真正的表头，不然先占位，避免blankcol从无到有引起的抖动问题 */}
            </table>
            {/* </div> */}
            {renderScrollBarBlank()}
        </div>
        {fixedTop && <div style={{
            height: ref.current.offsetHeight,
            width: bodyWidth,
        }}></div>}
    </>;

    /**
     * —————————————————————————————————————— functions ——————————————————————————————————————
     */

    function renderDragHandler(range) {
        return <div
            className="column-resize-handler"
            style={{ cursor: 'col-resize !important' }} //偶发出现鼠标标识不显示的问题
            onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                let lastCursor = document.body.style.cursor;
                document.body.style.cursor = 'col-resize';

                startX.current = e.screenX;
                startWidth.current = columnsWidth.slice(...range);

                let dragColumns = leafColumns.slice(...range);

                columnsWidthMap = { ...columnsWidthMap };

                function resize(e) {
                    e.stopPropagation();
                    e.preventDefault();
                    // 每一列平分宽度
                    let offset = (e.screenX - startX.current) / startWidth.current.length;
                    let nextRangeWidth = startWidth.current.map(e => Math.max(60, e + offset));

                    columnsWidthMap = dragColumns.reduce((columnsWidthMap, column, index) => {
                        columnsWidthMap[column.key] = nextRangeWidth[index];
                        return columnsWidthMap;
                    }, columnsWidthMap);

                    onColumnResize?.(columnsWidthMap);
                }

                const finished = () => {
                    document.body.style.cursor = lastCursor;
                    windowForSonar.removeEventListener('mousemove', resize);
                    windowForSonar.removeEventListener('mouseup', finished);
                };

                windowForSonar.addEventListener('mousemove', resize);
                windowForSonar.addEventListener('mouseup', finished);
            }}
        ></div>;
    }

    function renderScrollBarBlank() {
        return hasVerticalScrollBar && <div className="scroll-bar-blank" style={{ width: scrollBarSize }}></div>;
    }
});